import { cn } from 'cn';
import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type ToggleOption<TValue extends string> = {
  readonly disabled?: boolean;
  readonly label: ReactNode;
  readonly value: TValue;
};

type P<TValue extends string = string> = {
  readonly 'aria-label'?: string;
  readonly onChange: (value: TValue) => void;
  readonly options: ReadonlyArray<ToggleOption<TValue>>;
  readonly size?: 'md' | 'sm';
  readonly value: TValue;
} & ComponentStyling;

type IndicatorStyle = Pick<CSSProperties, 'width' | 'transform'>;

export const ElasticToggleGroup = <TValue extends string>({
  'aria-label': ariaLabel,
  className,
  onChange,
  options,
  size = 'md',
  style,
  value,
}: P<TValue>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const [interactiveValue, setInteractiveValue] = useState<string | null>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<IndicatorStyle>();

  const currentValue = interactiveValue ?? value;

  const updateIndicatorForValue = (targetValue: string | null) => {
    if (!targetValue || !containerRef.current) {
      return;
    }

    const button = buttonRefs.current.get(targetValue) ?? null;

    if (!button) {
      return;
    }

    const buttonRect = button.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const left = buttonRect.left - containerRect.left;

    setIndicatorStyle({
      width: `${buttonRect.width}px`,
      transform: `translateX(${left}px)`,
    });
  };

  useLayoutEffect(() => {
    // Use rAF to ensure layout is complete before measuring
    const frameId = requestAnimationFrame(() => {
      updateIndicatorForValue(currentValue);
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [currentValue, options.length]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Labels can change size after mount (counts arriving, fonts loading), which
    // moves the buttons without any prop or window change.
    const observer = new ResizeObserver(() => {
      updateIndicatorForValue(currentValue);
    });
    observer.observe(container);
    for (const button of buttonRefs.current.values()) {
      if (button) {
        observer.observe(button);
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [currentValue, options.length]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();

    const enabledOptions = options.filter((option) => !option.disabled);
    const currentIndex = enabledOptions.findIndex(
      (option) => option.value === value,
    );
    if (currentIndex === -1) {
      return;
    }

    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex =
      (currentIndex + direction + enabledOptions.length) %
      enabledOptions.length;

    const nextValue = enabledOptions[nextIndex]?.value;
    if (!nextValue) {
      return;
    }

    onChange(nextValue);
    setInteractiveValue(null);
  };

  return (
    <div
      ref={containerRef}
      aria-label={ariaLabel}
      className={cn(
        // The background lives on a pseudo-element so the multiply blend doesn't apply to the children.
        'relative inline-flex items-stretch rounded-full p-1 before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:bg-tiga-surface-off-white before:mix-blend-multiply',
        size === 'sm' ? 'h-7 text-xs' : 'h-10 text-sm',
        className,
      )}
      role="radiogroup"
      style={style}
      onKeyDown={handleKeyDown}
    >
      {indicatorStyle && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-1 left-0 rounded-full bg-white shadow transition-all duration-300 ease-out"
          style={indicatorStyle}
        />
      )}

      {options.map((option) => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            ref={(node) => {
              buttonRefs.current.set(option.value, node);
            }}
            className={cn(
              'relative z-10 inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap transition-colors duration-200',
              size === 'sm' ? 'px-2' : 'px-4',
              option.disabled
                ? 'cursor-not-allowed text-tiga-text-lighter'
                : isActive
                  ? 'cursor-default text-tiga-text-default'
                  : 'cursor-pointer text-tiga-action-default hover:text-tiga-text-default',
            )}
            data-elastic-value={option.value}
            disabled={option.disabled}
            role="radio"
            aria-checked={isActive}
            tabIndex={isActive ? 0 : -1}
            type="button"
            onMouseDown={() => {
              if (!isActive && !option.disabled) {
                onChange(option.value);
              }
              setInteractiveValue(null);
            }}
            onMouseEnter={() => {
              if (!option.disabled) {
                setInteractiveValue(option.value);
              }
            }}
            onMouseLeave={() => {
              setInteractiveValue(null);
            }}
            onFocus={() => {
              if (!option.disabled) {
                setInteractiveValue(option.value);
              }
            }}
            onBlur={() => {
              setInteractiveValue(null);
            }}
          >
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};
